import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { DollarSign, Shield, Target, BarChart3, TrendingUp, Home } from "lucide-react";
import { SocialShare } from "@/components/SocialShare";

const DistRange = () => {
  return (
    <div className="py-8">
      {/* Header */}
      <section className="bg-gradient-to-br from-primary to-accent text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-4 bg-primary-foreground text-primary">Income Range</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Dist Range Portfolios</h1>
            <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto mb-4">
              Premium Income for a Premium Investor
            </p>
            <div className="flex justify-center items-center gap-8 text-lg mb-6">
              <div>
                <span className="font-semibold">Target Yield:</span> 4%+
              </div>
              <div>
                <span className="font-semibold">Benchmark:</span> CPI + 1%
              </div>
            </div>
            <div className="flex justify-center">
              <SocialShare title="CBAM Dist Range Portfolios" variant="light" />
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Portfolio Overview</h2>
              <p className="text-lg text-muted-foreground mb-6">
                The CBAM Distribution Portfolios have been created to meet the needs of investors who place a high
                priority on receiving a reliable and sustainable income from their investments, rather than reinvesting it
                for growth.
              </p>
              <p className="text-muted-foreground mb-6">
                This approach is particularly suited to individuals who require a steady cash flow, whether to fund
                retirement, supplement other income sources, or meet ongoing personal or business expenditure.
              </p>
            </div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Key Features
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    4%+ target yield
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    Income distribution focus
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    Inflation-aware benchmarks
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    Capital preservation
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Portfolio Selection */}
      <section className="py-16 bg-muted/50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Choose Your Dist Range Portfolio</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Three income-focused portfolios designed to provide sustainable distributions while managing risk across different investor profiles.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <Badge className="w-fit mb-2 bg-green-100 text-green-800">Risk Level 2-3</Badge>
                <CardTitle>Dist-Low</CardTitle>
                <CardDescription>Conservative income approach for cautious investors</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span>Target Yield:</span>
                    <span className="font-semibold">4%+</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Benchmark:</span>
                    <span className="font-semibold">CPI + 1%</span>
                  </div>
                </div>
                <Button asChild className="w-full">
                  <Link to="/portfolios/dist-low">Learn More</Link>
                </Button>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <BarChart3 className="h-6 w-6 text-primary" />
                </div>
                <Badge className="w-fit mb-2 bg-yellow-100 text-yellow-800">Risk Level 3-4</Badge>
                <CardTitle>Dist-Medium</CardTitle>
                <CardDescription>Balanced income approach for moderate investors</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span>Target Yield:</span>
                    <span className="font-semibold">4%+</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Benchmark:</span>
                    <span className="font-semibold">CPI + 2%</span>
                  </div>
                </div>
                <Button asChild className="w-full">
                  <Link to="/portfolios/dist-medium">Learn More</Link>
                </Button>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="h-6 w-6 text-primary" />
                </div>
                <Badge className="w-fit mb-2 bg-orange-100 text-orange-800">Risk Level 4-5</Badge>
                <CardTitle>Dist-High</CardTitle>
                <CardDescription>Growth-oriented income for capital growth investors</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span>Target Yield:</span>
                    <span className="font-semibold">4%+</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Benchmark:</span>
                    <span className="font-semibold">CPI + 3%</span>
                  </div>
                </div>
                <Button asChild className="w-full">
                  <Link to="/portfolios/dist-high">Learn More</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Income Sources */}
      <section className="py-16 bg-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Example Income Sources</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="text-center">
              <CardHeader>
                <DollarSign className="h-8 w-8 text-primary mx-auto mb-2" />
                <CardTitle className="text-lg">Global Dividend Equities</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Established companies with consistent dividend payment history
                </p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardHeader>
                <BarChart3 className="h-8 w-8 text-primary mx-auto mb-2" />
                <CardTitle className="text-lg">High Yield & IG Bonds</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Fixed coupon payments at attractive yields
                </p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardHeader>
                <TrendingUp className="h-8 w-8 text-primary mx-auto mb-2" />
                <CardTitle className="text-lg">Infrastructure Funds</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Inflation-linked revenues from essential services
                </p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardHeader>
                <Home className="h-8 w-8 text-primary mx-auto mb-2" />
                <CardTitle className="text-lg">Property & REITs</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Rental income from diversified property portfolios
                </p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardHeader>
                <Shield className="h-8 w-8 text-primary mx-auto mb-2" />
                <CardTitle className="text-lg">Alternative Income</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Renewable energy assets and specialist lending
                </p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardHeader>
                <Target className="h-8 w-8 text-primary mx-auto mb-2" />
                <CardTitle className="text-lg">Multi-Asset Funds</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Diversified income across multiple asset classes
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Explore the Dist Range?
          </h2>
          <p className="text-xl mb-8 text-primary-foreground/90 max-w-2xl mx-auto">
            Speak to our investment team about how the Dist Range portfolios can provide sustainable income for your needs.
          </p>
          <Button size="lg" variant="secondary" asChild>
            <Link to="/contact">Contact Our Team</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default DistRange;